import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  AutoDepositBadge,
  Button,
  ConfirmationDialog,
  CompanyRoleBadge,
  ProfileCompanyBadge,
  FallbackCompanyIcon,
  FallbackGroupIcon,
  LoadingPage,
  DepositRiskThresholdSelect,
  type DepositRiskThresholdValue,
} from '@valiant/ui-shared'
import { Input } from '@valiant/ui-shared'
import { Label } from '@valiant/ui-shared'
import { Card, CardContent, CardHeader, CardTitle } from '@valiant/ui-shared'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@valiant/ui-shared'
import { Checkbox } from '@valiant/ui-shared'
import { Switch } from '@valiant/ui-shared'
import {
  useGetCompanyQuery,
  useGetCompanyStatsQuery,
  useGetGroupsQuery,
  useGetCompaniesQuery,
  useGetCompanyDocumentsQuery,
  useUpdateCompanyMutation,
  useUpdateCompanyDocumentMutation,
  useReassignCompanyMutation,
  CompanyStatus,
  type DepositRiskThreshold,
  DocumentType,
} from '@/generated/graphql'
import { toast } from 'sonner'
import { ExternalLink } from 'lucide-react'
import { useAuth } from '@/AuthProvider'
import { MainLayout } from '@/components/layout/MainLayout'
import { FileUpload } from '@valiant/ui-shared'

export function ManageCompany() {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'

  const [{ data: companyData, fetching: companyLoading }] = useGetCompanyQuery({
    variables: { companyId: companyId! },
    pause: !companyId,
  })

  const [{ data: companyStatsData }] = useGetCompanyStatsQuery({
    variables: { companyId: companyId! },
    pause: !companyId,
  })

  const [{ data: groupsData }] = useGetGroupsQuery()
  const [{ data: groupCompaniesData }] = useGetCompaniesQuery({
    variables: {
      filter: {
        groupId: companyData?.getCompany?.groupId,
      },
    },
    pause: !companyData?.getCompany?.groupId,
  })
  const [{ data: documentsData }] = useGetCompanyDocumentsQuery({
    variables: { companyId: companyId! },
    pause: !companyId,
  })

  const [updateCompanyResult, updateCompany] = useUpdateCompanyMutation()
  const [, updateCompanyDocument] = useUpdateCompanyDocumentMutation()
  const [, reassignCompany] = useReassignCompanyMutation()
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [isReassigning, setIsReassigning] = useState(false)

  const company = companyData?.getCompany
  const groups = useMemo(() => groupsData?.getGroups || [], [groupsData?.getGroups])
  const documentsData_raw = useMemo(
    () => documentsData?.getCompanyDocuments?.documents || [],
    [documentsData?.getCompanyDocuments?.documents],
  )
  const companyFolderUrl = useMemo(
    () => documentsData?.getCompanyDocuments?.folder || '',
    [documentsData?.getCompanyDocuments?.folder],
  )
  const inheritedParentCompany = documentsData?.getCompanyDocuments?.parentCompany
  const isProfileCompany = !!company?.parentCompanyId

  const eligibleParentCompanies = useMemo(
    () =>
      (groupCompaniesData?.getCompanies || []).filter(
        (groupCompany) => groupCompany.id !== companyId && !groupCompany.parentCompanyId,
      ),
    [groupCompaniesData?.getCompanies, companyId],
  )

  // Get all possible document types from enum and merge with database data
  const allDocumentTypes = useMemo(() => {
    // Define the desired order for document types
    const documentTypeOrder = [
      DocumentType.CertificateOfIncorporation,
      DocumentType.CompanyProofOfAddress,
      DocumentType.RegistryOfDirectors,
      DocumentType.DirectorId,
      DocumentType.DirectorProofOfAddress,
      DocumentType.BankStatements,
      DocumentType.InternationalWireTransferInformation,
    ]

    // Create a map for quick lookup of order indices
    const orderMap = new Map(documentTypeOrder.map((type, index) => [type, index]))

    return Object.values(DocumentType)
      .map((docType) => {
        // Find existing document data for this type
        const existingDoc = documentsData_raw.find((doc) => doc.type === docType)

        return {
          id: existingDoc?.id || `placeholder-${docType}`,
          companyId: companyId!,
          type: docType,
          link: existingDoc?.link || '',
          checked: existingDoc?.checked || false,
          createdAt: existingDoc?.createdAt || '',
          updatedAt: existingDoc?.updatedAt || '',
        }
      })
      .sort((a, b) => {
        const orderA = orderMap.get(a.type) ?? Number.MAX_SAFE_INTEGER
        const orderB = orderMap.get(b.type) ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
  }, [documentsData_raw, companyId])

  // Form state
  const [name, setName] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [selectedParentCompanyId, setSelectedParentCompanyId] = useState('')
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [director, setDirector] = useState('')
  const [country, setCountry] = useState('')
  const [address, setAddress] = useState('')
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<CompanyStatus>(CompanyStatus.Crypto)
  const [folder, setFolder] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [autoApprove, setAutoApprove] = useState(false)
  const [depositRiskThreshold, setDepositRiskThreshold] =
    useState<DepositRiskThresholdValue>('MEDIUM')

  // Original values for comparison
  const [originalRegistrationNumber, setOriginalRegistrationNumber] = useState('')
  const [originalDirector, setOriginalDirector] = useState('')
  const [originalCountry, setOriginalCountry] = useState('')
  const [originalAddress, setOriginalAddress] = useState('')
  const [originalWebsite, setOriginalWebsite] = useState('')
  const [originalFolder, setOriginalFolder] = useState('')
  const [originalAutoApprove, setOriginalAutoApprove] = useState(false)
  const [originalDepositRiskThreshold, setOriginalDepositRiskThreshold] =
    useState<DepositRiskThresholdValue>('MEDIUM')
  const [originalParentCompanyId, setOriginalParentCompanyId] = useState('')

  // Document state for local changes
  const [documentChanges, setDocumentChanges] = useState<Record<string, boolean>>({})
  const [linkChanges, setLinkChanges] = useState<Record<string, string>>({})

  // Track if main form has changes
  const hasMainFormChanges = useMemo(() => {
    if (!company) return false

    const currentStatus =
      company.tradeType === 'CRYPTO'
        ? CompanyStatus.Crypto
        : company.tradeType === 'CRYPTO_AND_FIAT'
          ? CompanyStatus.CryptoAndFiat
          : CompanyStatus.Deactivated

    const hasDocumentChanges = !isProfileCompany && Object.keys(documentChanges).length > 0
    const hasLinkChanges = !isProfileCompany && Object.keys(linkChanges).length > 0

    return (
      name !== company.company ||
      selectedGroupId !== company.groupId ||
      selectedParentCompanyId !== originalParentCompanyId ||
      registrationNumber !== originalRegistrationNumber ||
      director !== originalDirector ||
      country !== originalCountry ||
      address !== originalAddress ||
      website !== originalWebsite ||
      folder !== originalFolder ||
      status !== currentStatus ||
      imageUrl !== (company.imageUrl || '') ||
      autoApprove !== originalAutoApprove ||
      depositRiskThreshold !== originalDepositRiskThreshold ||
      hasDocumentChanges ||
      hasLinkChanges
    )
  }, [
    company,
    name,
    selectedGroupId,
    registrationNumber,
    originalRegistrationNumber,
    director,
    originalDirector,
    country,
    originalCountry,
    address,
    originalAddress,
    website,
    originalWebsite,
    folder,
    originalFolder,
    status,
    imageUrl,
    autoApprove,
    originalAutoApprove,
    depositRiskThreshold,
    originalDepositRiskThreshold,
    documentChanges,
    linkChanges,
    selectedParentCompanyId,
    originalParentCompanyId,
    isProfileCompany,
  ])

  // Set initial values when company data loads
  useEffect(() => {
    if (company) {
      setName(company.company)
      setSelectedGroupId(company.groupId)
      const parentId = company.parentCompanyId || ''
      setSelectedParentCompanyId(parentId)
      setOriginalParentCompanyId(parentId)
      setImageUrl(company.imageUrl || '')
      setAutoApprove(company.autoApprove)
      setOriginalAutoApprove(company.autoApprove)
      setDepositRiskThreshold(company.depositRiskThreshold as DepositRiskThresholdValue)
      setOriginalDepositRiskThreshold(company.depositRiskThreshold as DepositRiskThresholdValue)
      setStatus(
        company.tradeType === 'CRYPTO'
          ? CompanyStatus.Crypto
          : company.tradeType === 'CRYPTO_AND_FIAT'
            ? CompanyStatus.CryptoAndFiat
            : CompanyStatus.Deactivated,
      )
    }
  }, [company])

  // Set initial values from company stats
  useEffect(() => {
    if (companyStatsData?.getCompanyStats) {
      const stats = companyStatsData.getCompanyStats
      const regNum = stats.registrationNumber || ''
      const dir = stats.director || ''
      const ctry = stats.country || ''
      const addr = stats.address || ''
      const web = stats.website || ''

      setRegistrationNumber(regNum)
      setDirector(dir)
      setCountry(ctry)
      setAddress(addr)
      setWebsite(web)

      // Store original values for comparison
      setOriginalRegistrationNumber(regNum)
      setOriginalDirector(dir)
      setOriginalCountry(ctry)
      setOriginalAddress(addr)
      setOriginalWebsite(web)
    }
  }, [companyStatsData])

  // Set initial folder value from documents query
  useEffect(() => {
    if (companyFolderUrl) {
      setFolder(companyFolderUrl)
      setOriginalFolder(companyFolderUrl)
    }
  }, [companyFolderUrl])

  // Document handlers
  const handleDocumentCheckboxChange = (documentType: string, checked: boolean) => {
    setDocumentChanges((prev) => ({
      ...prev,
      [documentType]: checked,
    }))
  }

  const handleDocumentLinkClick = (link: string) => {
    if (link && link.trim()) {
      window.open(link, '_blank', 'noopener,noreferrer')
    }
  }

  // Helper function to get the current checkbox state (local changes override original state)
  const getDocumentCheckedState = (documentType: string, originalChecked: boolean) => {
    return documentType in documentChanges ? documentChanges[documentType] : originalChecked
  }

  // Helper function to get the current link value (local changes override original state)
  const getDocumentLinkValue = (documentType: string, originalLink: string) => {
    return documentType in linkChanges ? linkChanges[documentType] : originalLink
  }

  // Handler for document link changes
  const handleDocumentLinkChange = (documentType: string, link: string) => {
    setLinkChanges((prev) => ({
      ...prev,
      [documentType]: link,
    }))
  }

  const isSubmitting = updateCompanyResult.fetching || isReassigning

  const targetGroupName =
    groups.find((group) => group.groupId === selectedGroupId)?.name ?? 'the selected group'

  async function updateDocumentsForCompany(targetCompanyId: string) {
    if (isProfileCompany) {
      return
    }

    const hasDocumentChanges = Object.keys(documentChanges).length > 0
    const hasLinkChanges = Object.keys(linkChanges).length > 0

    if (!hasDocumentChanges && !hasLinkChanges) {
      return
    }

    const typesToUpdate = new Set([...Object.keys(documentChanges), ...Object.keys(linkChanges)])

    for (const documentType of typesToUpdate) {
      try {
        const input: {
          companyId: string
          documentType: DocumentType
          checked?: boolean
          link?: string
        } = {
          companyId: targetCompanyId,
          documentType: documentType as DocumentType,
        }

        if (documentType in documentChanges) {
          input.checked = documentChanges[documentType]
        }

        if (documentType in linkChanges) {
          input.link = linkChanges[documentType]
        }

        const docResult = await updateCompanyDocument({ input })

        if (docResult.error) {
          toast.error(`Failed to update ${documentType.replace(/_/g, ' ').toLowerCase()}`, {
            description: docResult.error.message,
          })
        }
      } catch (docError) {
        toast.error(`Failed to update ${documentType.replace(/_/g, ' ').toLowerCase()}`, {
          description:
            docError instanceof Error ? docError.message : 'An unexpected error occurred',
        })
      }
    }

    setDocumentChanges({})
    setLinkChanges({})
  }

  async function applyCompanyFieldUpdates(targetCompanyId: string) {
    if (!company) return

    const updateInput: {
      companyId: string
      name?: string
      registrationNumber?: string
      director?: string
      country?: string
      address?: string
      website?: string
      status?: CompanyStatus
      folder?: string
      imageUrl?: string
      autoApprove?: boolean
      depositRiskThreshold?: DepositRiskThreshold
      parentCompanyId?: string
    } = { companyId: targetCompanyId }

    let hasFieldChanges = false

    if (name !== company.company) {
      updateInput.name = name
      hasFieldChanges = true
    }
    if (registrationNumber !== originalRegistrationNumber) {
      updateInput.registrationNumber = registrationNumber
      hasFieldChanges = true
    }
    if (director !== originalDirector) {
      updateInput.director = director
      hasFieldChanges = true
    }
    if (country !== originalCountry) {
      updateInput.country = country
      hasFieldChanges = true
    }
    if (address !== originalAddress) {
      updateInput.address = address
      hasFieldChanges = true
    }
    if (website !== originalWebsite) {
      updateInput.website = website
      hasFieldChanges = true
    }
    if (!isProfileCompany && folder !== originalFolder) {
      updateInput.folder = folder
      hasFieldChanges = true
    }
    if (selectedParentCompanyId !== originalParentCompanyId) {
      updateInput.parentCompanyId = selectedParentCompanyId || ''
      hasFieldChanges = true
    }
    if (imageUrl !== (company.imageUrl || '')) {
      updateInput.imageUrl = imageUrl
      hasFieldChanges = true
    }
    if (autoApprove !== originalAutoApprove) {
      updateInput.autoApprove = autoApprove
      hasFieldChanges = true
    }
    if (depositRiskThreshold !== originalDepositRiskThreshold) {
      updateInput.depositRiskThreshold = depositRiskThreshold as DepositRiskThreshold
      hasFieldChanges = true
    }

    const currentStatus =
      company.tradeType === 'CRYPTO'
        ? CompanyStatus.Crypto
        : company.tradeType === 'CRYPTO_AND_FIAT'
          ? CompanyStatus.CryptoAndFiat
          : CompanyStatus.Deactivated
    if (status !== currentStatus) {
      updateInput.status = status
      hasFieldChanges = true
    }

    if (hasFieldChanges) {
      const result = await updateCompany({ input: updateInput })
      if (result.error) {
        throw new Error(result.error.message)
      }
    }

    await updateDocumentsForCompany(targetCompanyId)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!company) return

    if (selectedGroupId !== company.groupId) {
      setReassignDialogOpen(true)
      return
    }

    try {
      await applyCompanyFieldUpdates(companyId!)
      toast.success('Company updated successfully!')
    } catch (error) {
      toast.error('Failed to update company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleReassignConfirm = async () => {
    if (!company) return

    setIsReassigning(true)
    try {
      const result = await reassignCompany({
        input: { companyId: companyId!, groupId: selectedGroupId },
      })

      if (result.error) {
        toast.error('Failed to reassign company', {
          description: result.error.message,
        })
        return
      }

      const newCompanyId = result.data?.reassignCompany.id
      if (!newCompanyId) {
        toast.error('Failed to reassign company', {
          description: 'No company id returned from server',
        })
        return
      }

      await applyCompanyFieldUpdates(newCompanyId)
      setReassignDialogOpen(false)
      toast.success('Company reassigned successfully!')
      navigate(`/companies/${newCompanyId}/manage`)
    } catch (error) {
      toast.error('Failed to reassign company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    } finally {
      setIsReassigning(false)
    }
  }

  const getStatusColor = (status: CompanyStatus) => {
    return status === CompanyStatus.Crypto
      ? 'text-blue-600'
      : status === CompanyStatus.CryptoAndFiat
        ? 'text-green-600'
        : 'text-red-600'
  }

  const getStatusLabel = (status: CompanyStatus) => {
    return status === CompanyStatus.Crypto
      ? 'Crypto'
      : status === CompanyStatus.CryptoAndFiat
        ? 'Crypto & FIAT'
        : 'Deactivated'
  }

  const handleUploadRequest = async (_file: File) => {
    const result = await updateCompany({
      input: {
        companyId: companyId!,
        imageUrl: 'UPLOAD',
      },
    })

    if (result.error) {
      throw new Error(result.error.message)
    }

    // Check if the result has upload data (check for uploadData presence instead of __typename)
    const updateResult = result.data?.updateCompany
    const uploadData =
      updateResult && 'uploadData' in updateResult ? updateResult.uploadData : undefined

    if (uploadData) {
      return {
        uploadUrl: uploadData.uploadUrl,
        fileUrl: uploadData.fileUrl,
        fileName: uploadData.fileName,
      }
    }

    throw new Error('Upload data not received from server')
  }

  const handleImageUploaded = async (fileUrl: string) => {
    try {
      // Auto-save the image URL immediately
      const result = await updateCompany({
        input: {
          companyId: companyId!,
          imageUrl: fileUrl,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      // Update local state on success
      setImageUrl(fileUrl)
      toast.success('Company logo updated successfully')
    } catch (error) {
      toast.error('Failed to save company logo')
      console.error('Error saving company logo:', error)
    }
  }

  const badges = useMemo(() => {
    if (!company) return []
    const items = [
      {
        node: <CompanyRoleBadge tradeType={company?.tradeType} />,
        direction: 'LEFT' as const,
      },
      {
        node: <AutoDepositBadge autoApprove={company.autoApprove} />,
        direction: 'LEFT' as const,
      },
    ]

    if (company.parentCompanyId) {
      items.push({
        node: <ProfileCompanyBadge />,
        direction: 'LEFT',
      })
    }

    return items
  }, [company])

  if (companyLoading) {
    return <LoadingPage />
  }

  if (!company) {
    return (
      <MainLayout>
        <div>Company not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: company.group,
          onClick: () => navigate(`/groups/${company.groupId}`),
          imageUrl: company.groupImageUrl || undefined,
          fallbackImage: <FallbackGroupIcon />,
        },
        {
          label: company.company,
          onClick: () => navigate(`/companies/${companyId}`),
          imageUrl: company.imageUrl || undefined,
          fallbackImage: <FallbackCompanyIcon />,
          badges: badges,
        },
      ]}
      title="Manage Company"
    >
      <div className="grid gap-8 lg:grid-cols-2">
        <h2 className="md:hidden text-xl font-semibold">Manage Company</h2>
        {/* Manage Company Form */}
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter company name"
                  required
                  disabled={isComplianceUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="group">Group</Label>
                <Select
                  value={selectedGroupId}
                  onValueChange={(value) => {
                    if (value && value !== selectedGroupId) {
                      setSelectedGroupId(value)
                    }
                  }}
                  disabled={isComplianceUser}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((group) => (
                      <SelectItem key={group.groupId} value={group.groupId}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="parentCompany">Parent Company</Label>
                <Select
                  value={selectedParentCompanyId || 'none'}
                  onValueChange={(value) =>
                    setSelectedParentCompanyId(value === 'none' ? '' : value)
                  }
                  disabled={isComplianceUser || isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a parent company (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {eligibleParentCompanies.map((parentCompany) => (
                      <SelectItem key={parentCompany.id} value={parentCompany.id}>
                        {parentCompany.company}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="registrationNumber">Registration #</Label>
                <Input
                  id="registrationNumber"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  placeholder="Enter registration number"
                  disabled={isComplianceUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="director">Director</Label>
                <Input
                  id="director"
                  value={director}
                  onChange={(e) => setDirector(e.target.value)}
                  placeholder="Enter director name"
                  disabled={isComplianceUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Enter country"
                  disabled={isComplianceUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Enter address"
                  disabled={isComplianceUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="Enter website URL"
                  type="url"
                  disabled={isComplianceUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="folder">Company Folder</Label>
                <Input
                  id="folder"
                  value={folder}
                  onChange={(e) => setFolder(e.target.value)}
                  placeholder="Enter company folder URL (e.g., Google Drive folder)"
                  type="url"
                  disabled={isComplianceUser || isProfileCompany}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={status}
                  onValueChange={(value) => {
                    if (value && value !== status) {
                      setStatus(value as CompanyStatus)
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <span className={getStatusColor(status)}>{getStatusLabel(status)}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CompanyStatus.Crypto}>
                      <span className="text-blue-600">Crypto</span>
                    </SelectItem>
                    <SelectItem value={CompanyStatus.CryptoAndFiat}>
                      <span className="text-green-600">Crypto & FIAT</span>
                    </SelectItem>
                    <SelectItem value={CompanyStatus.Deactivated}>
                      <span className="text-red-600">Deactivated</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="autoApprove"
                    checked={autoApprove}
                    onCheckedChange={setAutoApprove}
                    disabled={isComplianceUser || isSubmitting}
                  />
                  <Label htmlFor="autoApprove" className="cursor-pointer">
                    Auto-approve deposits
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  When enabled, deposits from this company will be automatically approved without
                  manual review.
                </p>
              </div>

              <DepositRiskThresholdSelect
                value={depositRiskThreshold}
                onValueChange={setDepositRiskThreshold}
                disabled={isComplianceUser || isSubmitting}
                helperText="Deposits with risk above this level are flagged for compliance review before approval."
              />

              {/* Documents Section */}
              <div className="space-y-4 pt-6 border-t">
                {isProfileCompany && inheritedParentCompany && (
                  <div className="rounded-md border border-main/30 bg-main/5 p-3 text-sm">
                    Documents inherited from{' '}
                    <button
                      type="button"
                      className="font-medium text-main underline-offset-4 hover:underline"
                      onClick={() => navigate(`/companies/${inheritedParentCompany.id}/manage`)}
                    >
                      {inheritedParentCompany.name}
                    </button>
                    . Edit documents on the parent company.
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Label className="text-base font-semibold">Documents</Label>
                  <span className="text-xs text-muted-foreground">
                    ({allDocumentTypes.filter((doc) => doc.checked).length}/
                    {allDocumentTypes.length} completed)
                  </span>
                </div>

                {/* Required for Crypto status */}
                <div className="space-y-3">
                  <div className="text-sm font-medium text-muted-foreground">
                    Required for Crypto status
                  </div>
                  {[
                    'CERTIFICATE_OF_INCORPORATION',
                    'COMPANY_PROOF_OF_ADDRESS',
                    'REGISTRY_OF_DIRECTORS',
                    'DIRECTOR_ID',
                  ].map((docType) => {
                    const document = allDocumentTypes.find((doc) => doc.type === docType)
                    if (!document) return null

                    const currentLink = getDocumentLinkValue(document.type, document.link)

                    return (
                      <div key={document.id} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={document.id}
                            checked={getDocumentCheckedState(document.type, document.checked)}
                            onCheckedChange={(checked) =>
                              handleDocumentCheckboxChange(document.type, checked as boolean)
                            }
                            disabled={isSubmitting || isProfileCompany}
                          />
                          <label
                            htmlFor={document.id}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {document.type
                              .replace(/_/g, ' ')
                              .toLowerCase()
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </label>
                        </div>
                        <div className="flex items-center gap-2 pl-6">
                          {currentLink && currentLink.trim() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDocumentLinkClick(currentLink)}
                              disabled={isSubmitting}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Required for Crypto & FIAT status */}
                <div className="space-y-3 pt-4">
                  <div className="text-sm font-medium text-muted-foreground">
                    Required for Crypto & FIAT status
                  </div>
                  {[
                    'DIRECTOR_PROOF_OF_ADDRESS',
                    'BANK_STATEMENTS',
                    'INTERNATIONAL_WIRE_TRANSFER_INFORMATION',
                  ].map((docType) => {
                    const document = allDocumentTypes.find((doc) => doc.type === docType)
                    if (!document) return null

                    const currentLink = getDocumentLinkValue(document.type, document.link)

                    return (
                      <div key={document.id} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={document.id}
                            checked={getDocumentCheckedState(document.type, document.checked)}
                            onCheckedChange={(checked) =>
                              handleDocumentCheckboxChange(document.type, checked as boolean)
                            }
                            disabled={isSubmitting || isProfileCompany}
                          />
                          <label
                            htmlFor={document.id}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {document.type
                              .replace(/_/g, ' ')
                              .toLowerCase()
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </label>
                        </div>
                        <div className="flex items-center gap-2 pl-6">
                          {currentLink && currentLink.trim() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDocumentLinkClick(currentLink)}
                              disabled={isSubmitting}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-md"
                disabled={isSubmitting || !hasMainFormChanges}
                variant="main_action"
              >
                {isSubmitting ? 'Updating...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Setup Folders Card */}
        <Card className="justify-start">
          <CardHeader>
            <CardTitle>Setup Folders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyFolder">Company Folder</Label>
                <Input
                  id="companyFolder"
                  value={folder}
                  onChange={(e) => setFolder(e.target.value)}
                  placeholder="Enter company folder URL (e.g., Google Drive folder)"
                  type="url"
                  disabled={isComplianceUser || isProfileCompany}
                />
              </div>

              {/* Document link fields - dynamically generated and functional */}
              {allDocumentTypes.map((document) => {
                const currentLink = getDocumentLinkValue(document.type, document.link)
                return (
                  <div key={document.id} className="space-y-2">
                    <Label htmlFor={`document-link-${document.id}`}>
                      {document.type
                        .replace(/_/g, ' ')
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase())}{' '}
                      Link
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id={`document-link-${document.id}`}
                        value={currentLink}
                        onChange={(e) => handleDocumentLinkChange(document.type, e.target.value)}
                        placeholder={`Enter URL for ${document.type.replace(/_/g, ' ').toLowerCase()}`}
                        type="url"
                        disabled={isComplianceUser || isProfileCompany}
                        className="flex-1"
                      />
                      {currentLink && currentLink.trim() && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDocumentLinkClick(currentLink)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Company Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Company Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              onFileUploaded={handleImageUploaded}
              onError={(error) => toast.error('Upload failed', { description: error })}
              currentImageUrl={imageUrl}
              entityType="company"
              entityId={companyId!}
              maxSizeInMB={1}
              onUploadRequest={handleUploadRequest}
              disabled={isComplianceUser}
            />
          </CardContent>
        </Card>
      </div>

      <ConfirmationDialog
        open={reassignDialogOpen}
        onOpenChange={setReassignDialogOpen}
        title="Reassign company to another group?"
        description={`This will move "${company.company}" from ${company.group} to ${targetGroupName}, migrate swap/deposit history and whitelisted addresses to the new company, provision new deposit wallets, and assign ${targetGroupName}'s group owners. The old company will be archived and hidden from the UI.`}
        confirmText="Reassign"
        onConfirm={handleReassignConfirm}
        isLoading={isReassigning}
      />
    </MainLayout>
  )
}
